import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { getStudentProgressMe, type SkillProgressEntry } from '../../../services/api/canDo';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { useTutorQueryIdentity } from '../../tutor/hooks/useTutorQueryIdentity';
import { useTutorTenantLabels } from '../../tutor/hooks/useTutorTenantLabels';
import { communicationStudentProgressQueryKey } from '../queryKeys';
import { isDashboardIdentityReady } from '../utils/dashboardQueryGating';

export const COMMUNICATION_STUDENT_PROGRESS_QUERY_KEY = [
  'student-dashboard',
  'communication-student-progress',
] as const;

const STALE_TIME_MS = 5 * 60 * 1000;

export type CommunicationStudentProgressState = {
  skills: SkillProgressEntry[];
  loading: boolean;
  forbidden: boolean;
  error: Error | null;
  reload: () => void;
};

export function useCommunicationStudentProgress(options?: {
  enabled?: boolean;
}): CommunicationStudentProgressState {
  const { isSchoolFeatureEnabled, loading: featureLoading } = useFeatureAccess();
  const { labels, loading: labelsLoading } = useTutorTenantLabels();
  const { membershipKey, studentKey } = useTutorQueryIdentity();
  const identityReady = isDashboardIdentityReady(membershipKey, studentKey);

  const enabled =
    (options?.enabled ?? true) &&
    isSchoolFeatureEnabled('communication_tutor') &&
    !featureLoading &&
    !labelsLoading &&
    !labels.isMalay &&
    identityReady;

  const queryKey = identityReady
    ? communicationStudentProgressQueryKey({ membershipKey, studentKey })
    : [...COMMUNICATION_STUDENT_PROGRESS_QUERY_KEY, 'disabled'];

  const query = useQuery<{ skills: SkillProgressEntry[] }, Error>({
    queryKey,
    queryFn: async () => {
      const res = await getStudentProgressMe();
      return { skills: res.skills ?? [] };
    },
    enabled,
    staleTime: STALE_TIME_MS,
    retry: (failureCount, error) => {
      if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
        return false;
      }
      return failureCount < 1;
    },
  });

  const forbidden =
    isAxiosError(query.error) &&
    (query.error.response?.status === 401 || query.error.response?.status === 403);

  return {
    skills: query.data?.skills ?? [],
    loading: enabled && query.isLoading,
    forbidden,
    error: query.error,
    reload: () => void query.refetch(),
  };
}
